import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Play, Plus } from "lucide-react";
import VideoPlayer from "@/components/VideoPlayer";

const GamesListPage = () => {
  const { api, user } = useAuth();
  const [games, setGames] = useState([]);
  const [groupBy, setGroupBy] = useState("week");
  const canUpload = user?.role === "ysv_admin";

  useEffect(() => {
    api().get("/games").then(r => setGames(r.data)).catch(() => {});
  }, [api]);

  const fmt = (s) => `${Math.floor(s / 60)}m`;

  // Group by the calendar week (default — leagues run many games per week, so
  // months get crowded) or month of game_date. Groups newest-first, dateless
  // games last; within a group the list keeps the server's ordering. Weeks run
  // Sun–Sat and are keyed by the week's start date so keys sort chronologically.
  const monthKey = (g) => (g.game_date || "").slice(0, 7); // "2026-08" | ""
  const weekKey = (g) => {
    const iso = (g.game_date || "").split("T")[0];
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    if (isNaN(d)) return "";
    d.setDate(d.getDate() - d.getDay()); // back to Sunday
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const monthLabel = (key) => {
    if (!key) return "No game date";
    const [y, m] = key.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
  };
  const weekLabel = (key) => {
    if (!key) return "No game date";
    const start = new Date(key + "T00:00:00");
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const fmtD = (dt, withMonth) =>
      dt.toLocaleString("en-US", withMonth ? { month: "short", day: "numeric" } : { day: "numeric" });
    if (start.getFullYear() !== end.getFullYear()) {
      return `Week of ${fmtD(start, true)}, ${start.getFullYear()} – ${fmtD(end, true)}, ${end.getFullYear()}`;
    }
    return `Week of ${fmtD(start, true)} – ${fmtD(end, start.getMonth() !== end.getMonth())}, ${end.getFullYear()}`;
  };
  const keyFn = groupBy === "week" ? weekKey : monthKey;
  const labelFn = groupBy === "week" ? weekLabel : monthLabel;
  const dateGroups = [];
  {
    const byKey = new Map();
    for (const g of games) {
      const k = keyFn(g);
      if (!byKey.has(k)) byKey.set(k, []);
      byKey.get(k).push(g);
    }
    for (const k of [...byKey.keys()].sort((a, b) => (a === "" ? 1 : b === "" ? -1 : b.localeCompare(a)))) {
      dateGroups.push([k, byKey.get(k)]);
    }
  }

  return (
    <div data-testid="games-page" style={{ padding: '32px 40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 28, fontWeight: 700 }}>Games</h1>
          <div data-testid="games-groupby" role="group" aria-label="Group games by" style={{
            display: 'flex', borderRadius: 8, overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.14)',
          }}>
            {["week", "month"].map((mode) => (
              <button key={mode} type="button" data-testid={`games-groupby-${mode}`}
                onClick={() => setGroupBy(mode)}
                aria-pressed={groupBy === mode}
                style={{
                  padding: '5px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif", border: 'none', letterSpacing: 0.3,
                  background: groupBy === mode ? 'rgba(255,107,0,0.18)' : 'transparent',
                  color: groupBy === mode ? '#ff6b00' : 'var(--text-secondary)',
                }}
              >{mode === "month" ? "Month" : "Week"}</button>
            ))}
          </div>
        </div>
        {canUpload && <Link to="/games/new" data-testid="add-game-btn" style={{
          background: '#ff6b00', color: '#fff', textDecoration: 'none',
          padding: '8px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 6,
        }}><Plus size={16} /> Add Game</Link>}
      </div>

      {dateGroups.map(([key, groupGames]) => (
      <div key={key || "no-date"} data-testid={`games-group-${key || "none"}`} style={{ marginBottom: 20 }}>
        <h2 style={{
          fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 700,
          color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1.2,
          margin: '4px 0 10px', display: 'flex', alignItems: 'center', gap: 10,
        }}>
          {labelFn(key)}
          <span style={{
            background: 'rgba(255,107,0,0.12)', color: '#ff6b00', fontSize: 11,
            fontWeight: 800, padding: '2px 8px', borderRadius: 10, letterSpacing: 0,
          }}>{groupGames.length}</span>
        </h2>
        {groupGames.map(game => (
        <Link key={game.id} to={`/games/${game.id}`} data-testid={`game-item-${game.id}`} className="glass-card" style={{
          display: 'flex', alignItems: 'center', padding: '16px 20px',
          marginBottom: 8, textDecoration: 'none', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 12, background: 'rgba(255,107,0,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
            }}>
              <Play size={24} style={{ color: '#ff6b00' }} />
              <span style={{
                position: 'absolute', bottom: -2, right: -2, background: 'var(--cyan)',
                color: '#000', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
              }}>{fmt(game.duration_seconds)}</span>
            </div>
            <div>
              <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{game.title}</p>
              <p style={{ fontSize: 12, color: 'var(--cyan)' }}>vs {game.opponent}</p>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {game.game_date?.split('T')[0]} &bull; by {game.uploaded_by_name}
              </p>
            </div>
          </div>
          <div style={{
            width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,107,0,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Play size={20} style={{ color: '#ff6b00' }} />
          </div>
        </Link>
        ))}
      </div>
      ))}

      {games.length === 0 && (
        <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-secondary)' }}>
          <Play size={48} style={{ marginBottom: 12, opacity: 0.3 }} />
          <p>No games uploaded yet</p>
        </div>
      )}
    </div>
  );
};

const GameDetailPage = () => {
  const { gameId } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = React.useCallback(() => {
    api().get(`/games/${gameId}`).then(r => setGame(r.data)).catch(() => navigate("/games"));
  }, [gameId, api, navigate]);

  useEffect(() => { reload(); }, [reload]);

  const handleCreateClip = async (title, startTime, endTime, meta) => {
    try {
      // For multi-part games VideoPlayer passes the active segment's id so the
      // backend stores the clip with the correct video_url. start_time/end_time
      // are LOCAL to that segment (ClipPlayer seeks within that segment).
      await api().post("/clips", {
        game_id: gameId,
        title,
        start_time: startTime,
        end_time: endTime,
        tags: [],
        ...(meta?.segment_id ? { segment_id: meta.segment_id } : {}),
      });
      alert("Clip created successfully!");
    } catch (err) {
      alert("Failed to create clip");
    }
  };

  const canManage = game && (user?.role === "ysv_admin" || game.uploaded_by_user_id === user?.id);
  const isMulti = game && Array.isArray(game.segments) && game.segments.length > 0;
  const isLocked = !!game?.multi_part_locked;

  const markComplete = async () => {
    if (!window.confirm("Mark this game as complete? No more segments can be added until you re-open it.")) return;
    setBusy(true);
    try {
      await api().post(`/games/${gameId}/multi-part/complete`);
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Couldn't mark complete");
    } finally { setBusy(false); }
  };
  const reopen = async () => {
    if (!window.confirm("Re-open this game so you can add more segments?")) return;
    setBusy(true);
    try {
      await api().post(`/games/${gameId}/multi-part/reopen`);
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Couldn't re-open");
    } finally { setBusy(false); }
  };
  const addMorePartsHref = `/games/new?parent=${gameId}`;

  if (!game) return null;

  return (
    <div data-testid="game-detail-page" style={{ padding: '0' }}>
      {/* Multi-part toolbar — uploader / YSV admin only */}
      {isMulti && (canManage || isLocked) && (
        <div data-testid="game-multipart-toolbar" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
          padding: '10px 16px', gap: 10,
          background: isLocked ? 'rgba(76,175,80,0.08)' : 'rgba(0,229,255,0.06)',
          borderBottom: '1px solid ' + (isLocked ? 'rgba(76,175,80,0.3)' : 'rgba(0,229,255,0.25)'),
        }}>
          <p style={{ fontSize: 13, color: isLocked ? '#4caf50' : 'var(--cyan)', fontWeight: 700 }}>
            {isLocked
              ? `✓ Full game · ${game.segments.length} parts available`
              : `${game.segments.length} segment${game.segments.length === 1 ? "" : "s"} uploaded — more coming?`}
          </p>
          {canManage && (
            <div style={{ display: 'flex', gap: 8 }}>
              {!isLocked && (
                <button
                  data-testid="game-add-more-parts"
                  onClick={() => navigate(addMorePartsHref)}
                  style={{
                    padding: '6px 12px', background: 'transparent',
                    border: '1px solid rgba(0,229,255,0.4)', borderRadius: 8,
                    color: 'var(--cyan)', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >+ Add more parts</button>
              )}
              {!isLocked ? (
                <button
                  data-testid="game-mark-complete"
                  onClick={markComplete}
                  disabled={busy}
                  style={{
                    padding: '6px 12px', background: '#4caf50', border: 'none',
                    borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 800,
                    cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
                    fontFamily: "'Outfit', sans-serif",
                  }}
                >{busy ? "…" : "Mark game complete"}</button>
              ) : (
                <button
                  data-testid="game-reopen"
                  onClick={reopen}
                  disabled={busy}
                  style={{
                    padding: '6px 12px', background: 'transparent',
                    border: '1px solid rgba(255,152,0,0.4)', borderRadius: 8,
                    color: '#ff9800', fontSize: 12, fontWeight: 700,
                    cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >{busy ? "…" : "Re-open game"}</button>
              )}
            </div>
          )}
        </div>
      )}
      <VideoPlayer
        game={game}
        onBack={() => navigate("/games")}
        onCreateClip={handleCreateClip}
      />
    </div>
  );
};

export { GamesListPage, GameDetailPage };
